/** Observations panel for Today. Wave 1 W1.2: ambient pattern
 *  callouts ("Memso noticed Sarah's birthday is in 3 days") that the
 *  user Acknowledges, Archives, or marks False positive. Distinct from
 *  the Recommendations panel (which uses orange Accept primary
 *  buttons) by leaning on ghost styling and a subdued slate palette.
 *
 *  Renders nothing when the user has no `new` observations, so the
 *  panel does not occupy vertical real-estate on quiet days. */
import { useCallback, useEffect, useState } from "react";
import {
  Eye,
  Archive,
  Flag,
  Sparkle,
  Loader2,
  FileText,
} from "lucide-react";
import { api, type Observation, type ObservationKind } from "../lib/api";
import { useOpenPacket } from "./PacketViewerContext";

type ResolutionAction = "seen" | "archived" | "false_positive";

const KIND_LABEL: Record<ObservationKind, string> = {
  tone_shift: "Tone shift",
  relationship_warming: "Relationship warming",
  pattern_anomaly: "Pattern anomaly",
  topic_emergence: "Topic emerging",
  commitment_overdue: "Commitment overdue",
  decision_revisit: "Decision to revisit",
  question_unresolved: "Open question",
  frequency_unusual: "Cadence unusual",
  birthday_upcoming: "Birthday upcoming",
  deadline_approaching: "Deadline approaching",
};

export function ObservationsPanel() {
  const [items, setItems] = useState<Observation[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openPacket = useOpenPacket();

  const load = useCallback(() => {
    api
      .observations({ status: "new", limit: 20 })
      .then((d) => {
        setItems(d.observations);
        setLoaded(true);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoaded(true);
      });
  }, []);
  useEffect(load, [load]);

  if (!loaded) return null;
  if (items.length === 0 && !error) return null;

  const resolve = async (id: string, action: ResolutionAction) => {
    if (busy) return;
    setBusy(id);
    try {
      if (action === "seen") await api.acknowledgeObservation(id);
      else if (action === "archived") await api.archiveObservation(id);
      else await api.falsePositiveObservation(id);
      setItems((prior) => prior.filter((o) => o.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      aria-label="Observations"
      className="glass rounded-2xl p-4 sm:p-5 border border-slate-200/70 dark:border-slate-700/50 backdrop-blur space-y-3"
    >
      <header className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700/60 flex items-center justify-center text-slate-600 dark:text-slate-300">
          <Sparkle className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-sm font-bold text-slate-900 dark:text-slate-100">
            Memso noticed ({items.length})
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Ambient patterns Memso surfaced. Acknowledge to keep, Archive to hide, False positive to tune.
          </p>
        </div>
      </header>
      {error && (
        <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>
      )}
      <ul className="space-y-2">
        {items.map((obs) => (
          <li
            key={obs.id}
            className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-3 flex flex-col sm:flex-row sm:items-start gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200">
                  {KIND_LABEL[obs.kind]}
                </span>
                {typeof obs.confidence === "number" && (
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                    {Math.round(obs.confidence * 100)}% confidence
                  </span>
                )}
              </div>
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
                {obs.title}
              </div>
              {obs.body && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-line">
                  {obs.body}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {obs.source_packet_ids && obs.source_packet_ids.length > 0 && (
                <GhostButton
                  onClick={() => openPacket(obs.source_packet_ids[0])}
                  disabled={busy === obs.id}
                  Icon={FileText}
                  label="View source"
                />
              )}
              <GhostButton
                onClick={() => resolve(obs.id, "seen")}
                disabled={busy === obs.id}
                Icon={busy === obs.id ? Loader2 : Eye}
                label="Acknowledge"
                spinning={busy === obs.id}
              />
              <GhostButton
                onClick={() => resolve(obs.id, "archived")}
                disabled={busy === obs.id}
                Icon={Archive}
                label="Archive"
              />
              <GhostButton
                onClick={() => resolve(obs.id, "false_positive")}
                disabled={busy === obs.id}
                Icon={Flag}
                label="False positive"
                aria-label-only
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface GhostButtonProps {
  onClick: () => void;
  disabled: boolean;
  Icon: typeof Eye;
  label: string;
  spinning?: boolean;
  /** When true, the visible label is hidden on small screens; the
   *  aria-label keeps the affordance for screen readers and the icon
   *  carries the meaning. Used for the False positive button so the
   *  three-button row stays compact on mobile. */
  "aria-label-only"?: boolean;
}

function GhostButton({ onClick, disabled, Icon, label, spinning, ...rest }: GhostButtonProps) {
  const ariaLabelOnly = rest["aria-label-only"];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 bg-transparent text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700/40 disabled:opacity-50 transition-colors"
    >
      <Icon className={`w-3.5 h-3.5 ${spinning ? "animate-spin" : ""}`} />
      {!ariaLabelOnly && <span className="hidden sm:inline">{label}</span>}
    </button>
  );
}
